import cn from 'classnames';
import { KeyboardEvent as KeyboardEventReact, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@material-ui/core';
import Warning from 'shared/components/warning';
import { AKO, SYSTEM } from 'shared/constants/common';
import { BetslipEvents, setDataLayer } from 'shared/constants/datalayer';
import { defaultStakeValues, getBalanceDetails, getUserDetails, isLoggedin, isMobile } from 'shared/util/utility';
import Button from '../form/button';

import { StakeMethod } from 'api/betslipAnalytics.api';
import { getCustomStake } from 'features/login/store/login.action';
import { useDispatch, useSelector } from 'react-redux';
import { FINANCIAL_THRESHOLDS } from 'shared/constants/financial-thresholds';
import { BETSType, CouponState, State } from '../../interface';
import { getAnalyticalData } from '../../util/analytical';
import './styles.scss';

interface BetCalculatorCalculator {
	price: number;
	onCancelCal: () => void;
	onConfirmCal: (value: number) => void;
	betType: string;
	combination?: number;
	setStakeMethod: (stake: string) => void;
	bets: BETSType;
}

const BetCalculator = (props: BetCalculatorCalculator) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [betValue, setBetValue] = useState<string>(props.price.toString());
	const [notification, setNotification] = useState<boolean>(false);
	const [check, setCheck] = useState<boolean>(true);
	const [stakeType, setStakeType] = useState<string>('main');
	const [stakeButtonPressed, setStakeButtonPressed] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const inputRefMain = useRef<HTMLInputElement>(null);
	const inputRefCombination = useRef<HTMLInputElement>(null);
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const customStakeValues = useSelector<State, any>((state) => state.login.defaultStake);
	const [stakes, setStakes] = useState<number[]>(defaultStakeValues);

	useEffect(() => {
		setBetValue((props.bets.stake * (props.combination ? props.combination : 1)).toString());
	}, [props.bets.stake]);

	useEffect(() => {
		dispatch(getCustomStake());
	}, [dispatch]);

	useEffect(() => {
		if (customStakeValues) {
			setStakes(customStakeValues);
		} else {
			setStakes(defaultStakeValues);
		}
	}, [customStakeValues]);

	const parseNumberPad = (value: number | string) => {
		const price = value.toString();
		if (check && price !== '.') {
			setBetValue(`${price}`);
			setCheck(!check);
		} else {
			if (price === '.' && betValue.includes('.')) {
				setBetValue(`${betValue.toString()}`);
			} else {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				if (!(betValue.includes('.') && betValue.split('.')[1]!.length === 2)) {
					if (parseFloat(`${betValue.toString()}${price}`) <= 500000 && parseFloat(`${betValue.toString()}${price}`) > 0) {
						setBetValue(`${betValue.toString()}${price}`);
					} else {
						setNotification(true);
					}
				}
			}
		}
	};

	const writeValueFromKeyEvents = (event: KeyboardEventReact) => {
		const parsedEventKey = event.key.toString();

		if (/^[0-9.,]+$/.test(parsedEventKey)) {
			parsedEventKey === ',' ? parseNumberPad('.') : parseNumberPad(parsedEventKey);
		} else if (parsedEventKey === 'Backspace' || parsedEventKey === 'Delete') {
			removeNumber();
		} else if (parsedEventKey === 'Enter') {
			confirmCal();
		}
	};

	const removeNumber = () => {
		if (betValue) {
			const value = betValue.toString();
			setBetValue(value.slice(0, -1) || '');
			if (value.slice(0, -1) === '') {
				setNotification(true);
			}
		}
	};

	const valueDirect = (value: string) => {
		setCheck(true);
		setBetValue(value);
	};

	const confirmCal = () => {
		let value = 0;
		let parsedValue = Number(betValue) !== 0 ? Number(betValue) : 50;

		if (parsedValue < FINANCIAL_THRESHOLDS.MIN_STAKE || isNaN(parsedValue)) {
			parsedValue = FINANCIAL_THRESHOLDS.MIN_STAKE;
		}
		if (stakeType !== 'main' && props.combination) {
			value = parsedValue * props.combination;
		} else {
			value = parsedValue;
		}

		!stakeButtonPressed && props.setStakeMethod(StakeMethod.MANUAL);

		setDataLayer(BetslipEvents.BET_AMOUNT_CONFIRM(getAnalyticalData(coupon)));
		return props.onConfirmCal(value);
	};

	const convertValue = (type: string) => {
		if (type !== stakeType)
			if (stakeType !== 'main' && props.combination) {
				setBetValue((parseFloat(betValue) * props.combination).toFixed(2).toString());
			} else if (stakeType !== 'combination' && props.combination) {
				setBetValue((parseFloat(betValue) / props.combination).toFixed(2).toString());
			}
	};

	const changeStackType = (stackType: string) => {
		setStakeType(stackType);
		convertValue(stackType);
		if (!betValue) {
			setBetValue('0');
		}
	};

	const handleFocus = () => {
		if (isMobile()) {
			inputRef.current?.blur();
			inputRefCombination.current?.blur();
			inputRefMain.current?.blur();
		}
	};

	const onClickUseLastStake = () => {
		const lastStake = getUserDetails().lastStake;
		valueDirect(lastStake);
		setStakeButtonPressed(true);
		props.setStakeMethod(StakeMethod.LAST);
	};

	const onClickAllIn = () => {
		const { mainBalance, bonusBalance } = getBalanceDetails();
		const allInAmount = getBalanceDetails().selectedBalanceType === 'mainBalance' ? mainBalance : bonusBalance || '0';
		valueDirect(allInAmount);
		setStakeButtonPressed(true);
		props.setStakeMethod(StakeMethod.ALLIN);
	};
	const onClickPreDefinedStake = (amount: string) => {
		setStakeButtonPressed(true);
		valueDirect(amount);
		props.setStakeMethod(StakeMethod.DEFINED);
	};

	const onClickNumberPadBtns = (number: string) => {
		setStakeButtonPressed(false);
		parseNumberPad(number);
	};

	const onClickCancelCal = () => {
		props.onCancelCal();
		setDataLayer(BetslipEvents.BET_AMOUNT_CANCEL(getAnalyticalData(coupon)));
	};

	useEffect(() => {
		const handleTabKey = (event: KeyboardEvent) => {
			if (event.key === 'Tab') {
				event.preventDefault();
			}
		};

		window.addEventListener('keydown', handleTabKey);

		return () => {
			window.removeEventListener('keydown', handleTabKey);
		};
	}, []);

	return (
		<>
			<Modal open={true} className="BetCalculator" onClose={() => confirmCal()}>
				<div onClick={(e) => e.stopPropagation()} className="BetCalculator__wrapper">
					<div className="BetCalculator__container">
						{notification && (
							<Warning
								type="warning"
								className="!mx-0 !mt-0"
								title={`${t('warning')}!`}
								desc={`${t('amountWarning')}`}
								onClose={() => setNotification(false)}
							/>
						)}
						{props.betType === AKO && (
							<div className="BetCalculator__ako">
								<p className="BetCalculator__paragraph">{t('currentStack')}:</p>
								<div className="BetCalculator__rateSlider">
									<input
										className="BetCalculator__inputRate BetCalculator__inputRate--isNotSystem sss"
										autoFocus={!isMobile()}
										type="text"
										onFocus={handleFocus}
										ref={inputRef}
										value={betValue ? t('Common_decimal', { val: betValue }) : ''}
										onKeyDown={(event) => writeValueFromKeyEvents(event)}
									/>
								</div>
							</div>
						)}
						{props.betType === SYSTEM && (
							<div
								className={cn('BetCalculator__system', {
									'BetCalculator__system--isStackCardPrimary': stakeType === 'main',
								})}
								onClick={() => {
									changeStackType('main');
									inputRefMain.current?.focus();
								}}
							>
								<p className="BetCalculator__paragraph">{t('changeStakeSystem')}</p>
								<div className="BetCalculator__changeCombination">
									<input
										ref={inputRefMain}
										className={cn('BetCalculator__inputRate BetCalculator__inputRate--isSystem', {
											'BetCalculator__inputRate--isFocus': stakeType === 'main',
										})}
										autoFocus={!isMobile()}
										onFocus={handleFocus}
										value={
											betValue && props.combination
												? stakeType === 'main'
													? t('Common_decimal', { val: betValue })
													: t('Common_decimal', { val: Number(betValue) * props.combination })
												: ''
										}
										type="text"
										onKeyDown={(event) => writeValueFromKeyEvents(event)}
									/>
								</div>
							</div>
						)}
						{props.betType === SYSTEM && (
							<div
								className={cn('BetCalculator__system', {
									'BetCalculator__system--isStackCardPrimary': stakeType === 'combination',
								})}
								onClick={() => {
									changeStackType('combination');
									inputRefCombination.current?.focus();
								}}
							>
								<p className="BetCalculator__paragraph">{t('changeCombinationStakeSystem')}:</p>
								<div className="BetCalculator__changeCombination">
									<input
										ref={inputRefCombination}
										className={cn('BetCalculator__inputRate BetCalculator__inputRate--isSystem', {
											'BetCalculator__inputRate--isFocus': stakeType === 'combination',
										})}
										onFocus={handleFocus}
										value={
											betValue && props.combination
												? stakeType === 'combination'
													? t('Common_decimal', { val: betValue })
													: t('Common_decimal', { val: Number(betValue) / props.combination })
												: ''
										}
										type="text"
										onKeyDown={(event) => writeValueFromKeyEvents(event)}
									/>
								</div>
							</div>
						)}
						{isLoggedin() && props.betType === AKO && (
							<div className="BetCalculator__finalRate">
								<div className="BetCalculator__lastStakeWrapper" onClick={onClickUseLastStake}>
									{t('lastStack')}
								</div>
								<div className="BetCalculator__betAll" onClick={onClickAllIn}>
									<span>{t('betAny')}</span>
								</div>
							</div>
						)}
						<div className="BetCalculator__finalRate">
							{stakes.map((number: number, index: number) => (
								<div
									key={`number-${index}`}
									className="BetCalculator__valueItem"
									onClick={() => onClickPreDefinedStake(number.toString())}
									data-cy="bet-calc-proposal-stake"
								>
									{number}
								</div>
							))}
						</div>
						<p className="BetCalculator__enterRate">{t('enterRate')}</p>
						<div className="BetCalculator__customNumberKeys">
							{['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0'].map((number: string, index) => (
								<div
									key={`number-${index}`}
									className="BetCalculator__customKey"
									onClick={() => onClickNumberPadBtns(number)}
									data-cy="bet-calc-key"
								>
									<span>{number}</span>
								</div>
							))}
							<div className="BetCalculator__customKey BetCalculator__customKey--backText" onClick={() => removeNumber()}>
								<span>{t('backC')}</span>
							</div>
							<div className="BetCalculator__buttonsWrapper">
								<Button btnType="border" cyTestRef="calculator-cancel" onClick={onClickCancelCal}>
									{t('cancelC')}
								</Button>
								<Button btnType="default" cyTestRef="calculator-confirm" onClick={() => confirmCal()}>
									{t('confirm')}
								</Button>
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default BetCalculator;
